import { AppBar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { formatNumber } from '../utils/numbersFormatter'
import { useTranslation } from 'react-i18next'
import { ClientCashflow, ClientTaxPosition, ClientTaxPositionDeal, ClientTaxPositionDealTmp } from '../redux/reducers/clients'
import { NewLayout } from '../components/newLayout/NewLayout'
import { newThemeObject } from '../newTheme'
import { OrderDetailDialog } from '../components/dialogs/OrderDetailDialog'
import { FileImportDialog } from '../components/dialogs/FileImportDialog'
import CardWrapper from '../components/common/CardWrapper'
import { YearQuickFilter } from '../components/common/YearQuickFilter'
import classNames from 'classnames'
import { useSelector } from '../redux/hooks/useSelector'
import { LoadingIndicator } from '../components/common/LoadingIndicator'
import { useClientApi } from '../hooks/useClientApi'
import { getClientIdFromUser, isClientUser } from '../services/keycloak.service'
import { DatePicker, LocalizationProvider } from '@mui/lab'
import AdapterDateFns from '@date-io/date-fns'
import { ColoredIconButton } from '../components/common/ColoredIconButton'
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import { AlertMessageConfirmation } from '../components/dialogs/AlertMessageConfirmation'
import { tokens } from '../styles/tokens'
import { toApiDateTime } from '../utils/apiDateTime'
import useMobileWidth from '../hooks/useMobileWidth'

const CURRENT_YEAR = new Date().getFullYear()
const QUICK_FILTER_YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR]

const useStyles = makeStyles(theme => ({
  title: {
    color: tokens.color.white,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  paper: {
    overflowY: 'auto',
    overflowX: 'auto',
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  topBar: {
    background: `linear-gradient(135deg, ${tokens.color.primary[800]}, ${tokens.color.primary[600]})`,
    margin: '-16px -16px 20px',
    padding: '12px 16px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} 0 0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: '48px',
  },
  table: {
    minWidth: 1000,
  },
  tableHead: {
    ...newThemeObject.table.tableHead,
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  tableContentFirst: {
    ...newThemeObject.table.tableContentFirst,
  },
  lastRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  tableRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: tokens.color.neutral[200],
    },
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
  },

  tableTopBar: {
    backgroundColor: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    borderRadius: tokens.radius.lg,
    marginBottom: theme.spacing(2),
  },
  tableToolBar: {
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    alignContent: 'space-around',
    width: '100%',
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    margin: 0,
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  textField: {
    width: '100%',
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: theme.spacing(1.5),
      maxWidth: '180px',
    },
  },
  yearButtons: {
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: theme.spacing(1.5),
    },
  },
  filler: {
    flexGrow: 1,
  },
  icon: {
    marginLeft: theme.spacing(0.5),
  },
  firstClickableRow: {
    cursor: 'pointer',
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  // Karty pro mobil - nahrazuji sirokou tabulku, aby se nemuselo nic posouvat do stran.
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  mobileCard: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${tokens.color.neutral[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mobileCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(0.75, 1.5),
  },
  mobileCardField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  mobileCardLabel: {
    margin: 0,
    fontSize: '0.65rem',
    color: tokens.color.neutral[500],
  },
  mobileCardValue: {
    margin: '2px 0 0',
    fontSize: '0.75rem',
    fontWeight: 600,
    overflowWrap: 'break-word',
  },

}))

export const ClientDetailTaxPosition = () => {
  const classes = useStyles()
  const isMobile = useMobileWidth()

  const loading = useSelector(state => state.clients.loading)
  const client = useSelector(state => state.clients.client)
  const clientTaxPositionsTmp = useSelector(state => state.clients.clientTaxPosition)

  const { getClientTaxPosition, exportPosition } = useClientApi()
  const { getClient } = useClientApi()
  const { clientId } = useParams<{ clientId: string }>()
  const { t } = useTranslation()

  const getClientId = () => {
    if (isClientUser()) {
      return getClientIdFromUser()
    } else {
      return clientId
    }
  }
  
  const clientId2 = getClientId()
  useEffect(()=>{
    if ( !client ) {
      getClient(Number(clientId2))
    }
    if ( client && client.clientId!==0 ) {
      getClientTaxPosition(client.clientId);
    }
  }, [client])

  const [dateFrom, setDateFrom] = useState<Date>();
  const [dateTo, setDateTo] = useState<Date>();
  const [search, setSearch] = useState<string>();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const [clientTaxPositions, setClientTaxPositions] = useState<ClientTaxPositionDealTmp[]>([]);
  const [clientTaxOtherOperations, setClientTaxOtherOperations] = useState<ClientCashflow[]>([]);
  const [clientTaxWithholdingTaxes, setClientTaxWithholdingTaxes] = useState<ClientCashflow[]>([]);

  const onDateFromChange = (name: string) => (date: Date | null) => {
    setDateFrom(date || undefined)
    setSelectedYear(null)
  }

  const onDateToChange = (name: string) => (date: Date | null) => {
    setDateTo(date || new Date())
    setSelectedYear(null)
  }

  const onYearSelect = (year: number) => {
    const now = new Date()
    const isCurrentYear = year === now.getFullYear()
    setDateFrom(new Date(year, 0, 1))
    setDateTo(isCurrentYear ? now : new Date(year, 11, 31, 23, 59, 59, 999))
    setSelectedYear(year)
  }

  const onOrderTypeSearchChange = (event: React.ChangeEvent<{ value: string }>) => {
    setSearch(event.target.value)
  }

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const handleConfirmation = () => {
    exportPosition({deals: clientTaxPositions, withholdingTaxes: clientTaxWithholdingTaxes, otherOperations:clientTaxOtherOperations}, clientId, dateFrom ? toApiDateTime(dateFrom) : '', dateTo ? toApiDateTime(dateTo):'');
    setShowConfirmation(false);
  }

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  }

  useEffect(()=>{
    if ( clientTaxPositionsTmp && clientTaxPositionsTmp.deals ) {
      if ( clientTaxPositionsTmp.deals && clientTaxPositionsTmp.deals.length === 0 ) {
        setClientTaxPositions([]);
      } else {
        let tmp: ClientTaxPositionDealTmp[] = [];
        for (let i=0; i<clientTaxPositionsTmp.deals.length; i++) {
          if ( clientTaxPositionsTmp.deals[i].sells && clientTaxPositionsTmp.deals[i].sells.length > 0 ) {
            if ( dateFrom || dateTo || search ) {
              for ( let j=0; j<clientTaxPositionsTmp.deals[i].sells.length; j++) {
                if ( 
                  ( dateFrom && dateTo && new Date(clientTaxPositionsTmp.deals[i].sells[j].valueDate) >= dateFrom && new Date(clientTaxPositionsTmp.deals[i].sells[j].valueDate) <= dateTo ) ||
                  ( dateFrom && !dateTo && new Date(clientTaxPositionsTmp.deals[i].sells[j].valueDate) >= dateFrom ) || 
                  ( !dateFrom && dateTo && new Date(clientTaxPositionsTmp.deals[i].sells[j].valueDate) <= dateTo ) || 
                  ( search && clientTaxPositionsTmp.deals[i].sells[j].isin && clientTaxPositionsTmp.deals[i].sells[j].isin.toLowerCase().includes(search.toLowerCase()) )
                ) {
                  tmp.push(clientTaxPositionsTmp.deals[i]);
                  break;
                }
              }
            } else {
              tmp.push(clientTaxPositionsTmp.deals[i]);
            }
          }
        }
        setClientTaxPositions(tmp);
      }
    }
    if ( clientTaxPositionsTmp && clientTaxPositionsTmp.otherOperations ) {
      if ( clientTaxPositionsTmp.otherOperations && clientTaxPositionsTmp.otherOperations.length === 0 ) {
        setClientTaxOtherOperations([]);
      } else {
        let tmp: ClientCashflow[] = [];
        if ( dateFrom || dateTo || search ) {
          for (let i=0; i<clientTaxPositionsTmp.otherOperations.length; i++) {
            if ( 
              ( dateFrom && dateTo && new Date(clientTaxPositionsTmp.otherOperations[i].valueDate) >= dateFrom && new Date(clientTaxPositionsTmp.otherOperations[i].valueDate) <= dateTo ) ||
              ( dateFrom && !dateTo && new Date(clientTaxPositionsTmp.otherOperations[i].valueDate) >= dateFrom ) || 
              ( !dateFrom && dateTo && new Date(clientTaxPositionsTmp.otherOperations[i].valueDate) <= dateTo ) || 
              ( search && clientTaxPositionsTmp.otherOperations[i].text && clientTaxPositionsTmp.otherOperations[i].text.toLowerCase().includes(search.toLowerCase()) )
            ) {
              tmp.push(clientTaxPositionsTmp.otherOperations[i]);
            }
          }
        } else {
          tmp = [...clientTaxPositionsTmp.otherOperations];
        }
        setClientTaxOtherOperations(tmp);
      }
    }
    if ( clientTaxPositionsTmp && clientTaxPositionsTmp.withholdingTaxes ) {
      if ( clientTaxPositionsTmp.withholdingTaxes && clientTaxPositionsTmp.withholdingTaxes.length === 0 ) {
        setClientTaxWithholdingTaxes([]);
      } else {
        let tmp: ClientCashflow[] = [];
        if ( dateFrom || dateTo || search ) {
          for (let i=0; i<clientTaxPositionsTmp.withholdingTaxes.length; i++) {
            if ( 
              ( dateFrom && dateTo && new Date(clientTaxPositionsTmp.withholdingTaxes[i].valueDate) >= dateFrom && new Date(clientTaxPositionsTmp.withholdingTaxes[i].valueDate) <= dateTo ) ||
              ( dateFrom && !dateTo && new Date(clientTaxPositionsTmp.withholdingTaxes[i].valueDate) >= dateFrom ) || 
              ( !dateFrom && dateTo && new Date(clientTaxPositionsTmp.withholdingTaxes[i].valueDate) <= dateTo ) || 
              ( search && clientTaxPositionsTmp.withholdingTaxes[i].text && clientTaxPositionsTmp.withholdingTaxes[i].text.toLowerCase().includes(search.toLowerCase()) )
            ) {
              tmp.push(clientTaxPositionsTmp.withholdingTaxes[i]);
            }
          }
        } else {
          tmp = [...clientTaxPositionsTmp.withholdingTaxes];
        }
        setClientTaxWithholdingTaxes(tmp);
      }
    }
  }, [clientTaxPositionsTmp, dateFrom, dateTo, search])

  return (
    <NewLayout pageName="TAX_REPORT_TRANSACTION">

      <AppBar position="static" className={classes.tableTopBar} elevation={0}>
      <Toolbar variant="dense" className={classes.tableToolBar}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => <TextField {...params} variant="standard" className={classes.textField} label={t('DATE_FROM2')}/>}
            inputFormat="dd/MM/yyyy"
            value={dateFrom || null}
            onChange={onDateFromChange('dateFromId')}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => (
              <TextField {...params} variant="standard" className={classes.textField} label={t('DATE_TO')} />
            )}
            inputFormat="dd/MM/yyyy"
            value={dateTo || null}
            onChange={onDateToChange('dateToId')}
          />
        </LocalizationProvider>

        <YearQuickFilter
          className={classes.yearButtons}
          years={QUICK_FILTER_YEARS}
          selectedYear={selectedYear}
          onSelectYear={onYearSelect}
        />

        <TextField
          variant="standard"
          className={classes.textField}
          placeholder={t('SEARCH')}
          label={t('SEARCH')}
          onChange={onOrderTypeSearchChange}
          value={search}
        />

        <span className={classes.filler} />

        <ColoredIconButton
          className={classes.icon}
          onClick={() => {
            setShowConfirmation(true);
          }}
          size="small"
          icon={<DownloadIcon />}
        />
      </Toolbar>
      </AppBar>

      {loading && <LoadingIndicator />}
      {!loading && clientTaxPositions && clientTaxPositions.length > 0 &&
        clientTaxPositions.map((position: ClientTaxPositionDealTmp, securityIndex: number) => (
          <React.Fragment key={'taxPosition_'+securityIndex}>
            <TableContainer component={CardWrapper} className={classes.paper} key={66}>
              <span className={classes.topBar}>
                <p className={classes.title}>{t("TAX_REPORT_TRANSACTION")}</p>
                <p className={classes.title}>{(position.profitLoss > 0 ? '+' : '') + formatNumber(position.profitLoss) + ' ' + position.buy.currency}</p>
              </span>
              {isMobile ? (
                <div className={classes.mobileList}>
                  <div className={classes.mobileCard}>
                    <div className={classes.mobileCardGrid}>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('FIRST_NAME')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.firstName}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('SURNAME')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.surname}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('PORTFOLIO')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.portfolio}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('TRADE_DATE')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.tradeDate}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('VALUE_DATE')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.valueDate}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('DEAL_TYPE')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.tradeType}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('ISIN')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.isin}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('TICKER')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.ticker}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('QUANTITY')}</p>
                        <p className={classes.mobileCardValue}>{formatNumber(position.buy.quantity)}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('QUANTITY_FILL')}</p>
                        <p className={classes.mobileCardValue}>{formatNumber(position.buy.quantityFill)}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('FEE')}</p>
                        <p className={classes.mobileCardValue}>{formatNumber(position.buy.fee)}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('CURRENCY')}</p>
                        <p className={classes.mobileCardValue}>{position.buy.currency}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                        <p className={classes.mobileCardValue}>{formatNumber(position.buy.amount)}</p>
                      </div>
                    </div>
                  </div>
                  {position.sells.length > 0 && position.sells.map((deal: ClientTaxPositionDeal, index: number) => (
                    <div className={classes.mobileCard} key={index + '_inner'}>
                      <div className={classes.mobileCardGrid}>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('FIRST_NAME')}</p>
                          <p className={classes.mobileCardValue}>{deal.firstName}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('SURNAME')}</p>
                          <p className={classes.mobileCardValue}>{deal.surname}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('PORTFOLIO')}</p>
                          <p className={classes.mobileCardValue}>{deal.portfolio}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('TRADE_DATE')}</p>
                          <p className={classes.mobileCardValue}>{deal.tradeDate}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('VALUE_DATE')}</p>
                          <p className={classes.mobileCardValue}>{deal.valueDate}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('DEAL_TYPE')}</p>
                          <p className={classes.mobileCardValue}>{deal.tradeType}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('ISIN')}</p>
                          <p className={classes.mobileCardValue}>{deal.isin}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('TICKER')}</p>
                          <p className={classes.mobileCardValue}>{position.buy.ticker}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('QUANTITY')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(deal.quantity)}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('QUANTITY_FILL')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(deal.quantityFill)}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('FEE')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(deal.fee)}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('CURRENCY')}</p>
                          <p className={classes.mobileCardValue}>{deal.currency}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(deal.amount)}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('TAX_FREE')}</p>
                          <p className={classes.mobileCardValue}>{t(deal.freeTax ? 'YES' : 'NO')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <Table className={classes.table} size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                      <p className={classes.tableHeadChildrenText}>{t('FIRST_NAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('SURNAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('PORTFOLIO')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('TRADE_DATE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('VALUE_DATE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('DEAL_TYPE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('ISIN')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('TICKER')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('QUANTITY')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('QUANTITY_FILL')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('FEE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('AMOUNT')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('TAX_FREE')}</p>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow className={classes.firstClickableRow}>
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{position.buy.firstName}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.surname}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.portfolio}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.tradeDate}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.valueDate}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.tradeType}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.isin}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.ticker}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{formatNumber(position.buy.quantity)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{formatNumber(position.buy.quantityFill)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{formatNumber(position.buy.fee)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{position.buy.currency}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{formatNumber(position.buy.amount)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}></TableCell>
                    </TableRow>
                    { position.sells.length > 0 && (
                      position.sells.map((deal: ClientTaxPositionDeal, index: number) => (
                    <React.Fragment key={index+'_inner'}>
                      <TableRow
                        className={classes.tableRow}
                      >
                        <TableCell className={classes.tableContentFirst}>
                          <p className={classes.text}>{deal.firstName}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{deal.surname}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{deal.portfolio}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{deal.tradeDate}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{deal.valueDate}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{deal.tradeType}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{deal.isin}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position.buy.ticker}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(deal.quantity)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(deal.quantityFill)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(deal.fee)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{deal.currency}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(deal.amount)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{t(deal.freeTax?'YES':'NO')}</p>
                        </TableCell>

                      </TableRow>
                    </React.Fragment>
                  )))}
                </TableBody>
              </Table>
              )}
            </TableContainer>
          </React.Fragment>
      ))}

      {!loading && clientTaxWithholdingTaxes && clientTaxWithholdingTaxes.length > 0 &&
        <React.Fragment key={'withHolding_01'}>
          <TableContainer component={CardWrapper} className={classes.paper} key={66}>
            <span className={classes.topBar}>
              <p className={classes.title}>{t("WITHHOLDING_TAXES")}</p>
            </span>
            {isMobile ? (
              <div className={classes.mobileList}>
                {clientTaxWithholdingTaxes.map((clientDeal: ClientCashflow, index: number) => (
                  <div className={classes.mobileCard} key={index}>
                    <div className={classes.mobileCardGrid}>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('FIRST_NAME')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.firstName}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('SURNAME')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.surname}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('PORTFOLIO')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.portfolio}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                        <p className={classes.mobileCardValue}>{formatNumber(clientDeal.amount)}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('CURRENCY')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.currency}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('TRADE_DATE')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.tradeDate}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('VALUE_DATE')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.valueDate}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('TEXT')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.text}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('DESCRIPTION')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.tpid}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                    <p className={classes.tableHeadChildrenText}>{t('FIRST_NAME')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('SURNAME')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('PORTFOLIO')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('AMOUNT')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('TRADE_DATE')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('VALUE_DATE')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('TEXT')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('DESCRIPTION')}</p>
                  </TableCell>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenLast])} />
                </TableRow>
              </TableHead>
              <TableBody>
                {clientTaxWithholdingTaxes.map((clientDeal: ClientCashflow, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{clientDeal.firstName}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.surname}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.portfolio}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{formatNumber(clientDeal.amount)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.tradeDate}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.valueDate}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.text}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.tpid}</p>
                      </TableCell>

                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            )}
          </TableContainer>
        </React.Fragment>
      }

      {!loading && clientTaxOtherOperations && clientTaxOtherOperations.length > 0 &&
        <React.Fragment key={'otherOperation_01'}>
          <TableContainer component={CardWrapper} className={classes.paper} key={66}>
            <span className={classes.topBar}>
              <p className={classes.title}>{t("OTHER_OPERATIONS")}</p>
            </span>
            {isMobile ? (
              <div className={classes.mobileList}>
                {clientTaxOtherOperations.map((clientDeal: ClientCashflow, index: number) => (
                  <div className={classes.mobileCard} key={index}>
                    <div className={classes.mobileCardGrid}>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('FIRST_NAME')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.firstName}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('SURNAME')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.surname}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('PORTFOLIO')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.portfolio}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('AMOUNT')}</p>
                        <p className={classes.mobileCardValue}>{formatNumber(clientDeal.amount)}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('CURRENCY')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.currency}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('TRADE_DATE')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.tradeDate}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('VALUE_DATE')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.valueDate}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('TEXT')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.text}</p>
                      </div>
                      <div className={classes.mobileCardField}>
                        <p className={classes.mobileCardLabel}>{t('DESCRIPTION')}</p>
                        <p className={classes.mobileCardValue}>{clientDeal.tpid}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                    <p className={classes.tableHeadChildrenText}>{t('FIRST_NAME')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('SURNAME')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('PORTFOLIO')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('AMOUNT')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('TRADE_DATE')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('VALUE_DATE')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('TEXT')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren}>
                    <p className={classes.tableHeadChildrenText}>{t('DESCRIPTION')}</p>
                  </TableCell>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenLast])} />
                </TableRow>
              </TableHead>
              <TableBody>
                {clientTaxOtherOperations.map((clientDeal: ClientCashflow, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{clientDeal.firstName}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.surname}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.portfolio}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{formatNumber(clientDeal.amount)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.tradeDate}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.valueDate}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.text}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent}>
                        <p className={classes.text}>{clientDeal.tpid}</p>
                      </TableCell>

                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            )}
          </TableContainer>
        </React.Fragment>
      }

      <OrderDetailDialog />
      <FileImportDialog />
      <AlertMessageConfirmation
        alertOpen={showConfirmation}
        onOk={handleConfirmation}
        onCancle={handleCloseConfirmation}
        alertText={t('TAX_POSITION_EXPORT_MESSAGE')}
      />
    </NewLayout>
  )
}
